import { useTranslation } from 'react-i18next';
import { LogoutButton } from '@/components/common/LogoutButton';
import { LanguageSelector } from './accessShared';

export type NoPlatformAccessVariant = 'no-modules' | 'unregistered' | 'profile-error';

interface NoPlatformAccessViewProps {
  variant: NoPlatformAccessVariant;
  email?: string;
  /** Reintentar es RECARGA DE DOCUMENTO, nunca un refetch — el perfil se cachea en una variable de
   *  módulo (`getCurrentUserProfile`, api/client.ts) y solo una carga completa la vacía. */
  onRetry?: () => void;
}

const COPY_BY_VARIANT: Record<NoPlatformAccessVariant, {
  icon: string; titleKey: string; descKey: string;
}> = {
  'no-modules':    { icon: 'fa-lock',                 titleKey: 'access.noPlatformTitle',   descKey: 'access.noPlatformDesc' },
  'unregistered':  { icon: 'fa-lock',                 titleKey: 'access.noPlatformTitle',   descKey: 'access.noPlatformDesc' },
  'profile-error': { icon: 'fa-triangle-exclamation', titleKey: 'access.profileErrorTitle', descKey: 'access.profileErrorDesc' },
};

/**
 * Pantalla "No tienes acceso a la plataforma de cultura" — reemplaza el catálogo de Fundamentales
 * cuando alguien llega ahí por descarte: sin ficha en el directorio (`unregistered`), con ficha
 * pero ningún módulo habilitado (`no-modules`), o con el perfil degradado (`profile-error`, la
 * única variante con Reintentar como acción primaria).
 *
 * Reutiliza el CSS de las pantallas de acceso (.acc-*, recognition.css) en vez de portar el de la
 * maqueta del cliente: es el mismo lenguaje visual, ya con tema oscuro resuelto.
 */
export function NoPlatformAccessView({ variant, email, onRetry }: Readonly<NoPlatformAccessViewProps>) {
  const { t } = useTranslation();
  const { icon, titleKey, descKey } = COPY_BY_VARIANT[variant];

  const retryButton = (
    <button
      type="button"
      className="gate-action-btn"
      onClick={onRetry ?? (() => window.location.reload())}
    >
      <i className="fas fa-rotate-right" aria-hidden="true" />
      <span>{t('common.retry')}</span>
    </button>
  );
  const logoutButton = <LogoutButton variant="inline" />;

  return (
    <div className="acc-standalone">
      <div className="acc-page">
        <div className="acc-topbar"><LanguageSelector /></div>
        <div className="acc-card">
          <div className="acc-icon-wrap">
            <i className={`fas ${icon}`} aria-hidden="true" />
          </div>
          <h1 className="acc-title">{t(titleKey)}</h1>
          <p className="acc-subtitle">{t(descKey)}</p>
          {email && (
            <div className="acc-account">
              <i className="fas fa-envelope" aria-hidden="true" />
              <span>{email}</span>
            </div>
          )}
          <div className="gate-actions">
            {variant === 'profile-error'
              ? <>{retryButton}{logoutButton}</>
              : <>{logoutButton}{retryButton}</>}
          </div>
        </div>
        <div className="acc-stripe" aria-hidden="true">
          <span /><span /><span /><span />
        </div>
      </div>
    </div>
  );
}

export default NoPlatformAccessView;
